import { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ── Polskie święta ────────────────────────────────────────────────────────────
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function getPolishHolidays(year) {
  const easter = easterSunday(year);
  const add = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

  const fixed = [
    [1,  1,  'Nowy Rok'],
    [1,  6,  'Trzech Króli'],
    [5,  1,  'Święto Pracy'],
    [5,  3,  'Święto Konstytucji'],
    [8,  15, 'Wniebowzięcie NMP'],
    [11, 1,  'Wszystkich Świętych'],
    [11, 11, 'Święto Niepodległości'],
    ...(year >= 2025 ? [[12, 24, 'Wigilia']] : []),
    [12, 25, 'Boże Narodzenie'],
    [12, 26, 'Drugi dzień Bożego Narodzenia'],
  ];

  const holidays = {};
  fixed.forEach(([m, d, name]) => {
    holidays[`${year}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`] = name;
  });

  holidays[fmt(easter)]         = 'Wielkanoc';
  holidays[fmt(add(easter, 1))] = 'Poniedziałek Wielkanocny';
  holidays[fmt(add(easter, 49))]= 'Zesłanie Ducha Świętego';
  holidays[fmt(add(easter, 60))]= 'Boże Ciało';

  return holidays;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const DAY_NAMES = ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'];
const MONTH_NAMES = ['Styczeń','Luty','Marzec','Kwiecień','Maj','Czerwiec',
                     'Lipiec','Sierpień','Wrzesień','Październik','Listopad','Grudzień'];
const MONTH_NAMES_EN = ['January','February','March','April','May','June',
                        'July','August','September','October','November','December'];

// Paleta kolorów plakietek — indeks 0 zarezerwowany dla Asi (niebieski)
const PERSON_COLORS = [
  { bg: '#1c3a5e', color: '#58a6ff' }, // niebieski       — Asia
  { bg: '#1a3a2a', color: '#3fb950' }, // zielony
  { bg: '#2d1f5e', color: '#a371f7' }, // fioletowy
  { bg: '#0e3038', color: '#39c5cf' }, // turkusowy
  { bg: '#3a1a2e', color: '#f778ba' }, // różowy
  { bg: '#38320e', color: '#e3b341' }, // żółty
  { bg: '#3a1a1a', color: '#ff7b72' }, // czerwony
  { bg: '#2a1f10', color: '#f0883e' }, // pomarańczowy
  { bg: '#0e2a1a', color: '#56d364' }, // jasnozielony
  { bg: '#1a1040', color: '#d2a8ff' }, // lawendowy
  { bg: '#003030', color: '#76e3ea' }, // cyjan
  { bg: '#3a0e2e', color: '#ff9ec4' }, // jasnoróżowy
  { bg: '#2a2800', color: '#f5c542' }, // złoty
  { bg: '#1e0a0a', color: '#ffa198' }, // łososiowy
  { bg: '#0a1e2a', color: '#79c0ff' }, // błękitny
  { bg: '#1a2e10', color: '#b5e48c' }, // miętowy
];

function nameHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

function getPersonColor(osoba_nazwa, user_id, personColorMap) {
  const name = osoba_nazwa?.trim() || '';
  if (name.toLowerCase().startsWith('asia')) return PERSON_COLORS[0];
  let idx = user_id != null ? personColorMap[`user:${user_id}`] : undefined;
  if (idx === undefined) idx = personColorMap[`name:${name}`];
  if (idx === undefined) idx = nameHash(name) % PERSON_COLORS.length;
  return PERSON_COLORS[idx % PERSON_COLORS.length];
}

function toIso(date) {
  return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function isWorkdayIso(iso, holidays) {
  const date = new Date(iso);
  const dow = date.getDay();
  return dow !== 0 && dow !== 6 && !holidays[iso];
}

function getWorkDatesInYear(dataOd, dataDo, year, holidays) {
  const dates = [];
  const start = new Date(dataOd);
  const end = new Date(dataDo);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = toIso(d);
    if (d.getFullYear() === year && isWorkdayIso(iso, holidays)) dates.push(iso);
  }
  return dates;
}

function getPersonKey(userId, name) {
  return userId != null ? `user:${userId}` : `name:${(name || '').trim()}`;
}

// ── Modal: Dodaj urlop ────────────────────────────────────────────────────────
function AddUrlopModal({ users, onSave, onClose }) {
  const [mode, setMode]         = useState('user'); // 'user' | 'other'
  const [typ, setTyp]           = useState('URLOP'); // 'URLOP' | 'L4'
  const [userId, setUserId]     = useState(users[0]?.id || '');
  const [customName, setCustomName] = useState('');
  const [dataOd, setDataOd]     = useState('');
  const [dataDo, setDataDo]     = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleSave = async () => {
    const osoba_nazwa = mode === 'user'
      ? users.find(u => u.id === parseInt(userId))?.imie || ''
      : customName.trim();

    if (!osoba_nazwa) return setError('Podaj imię osoby');
    if (!dataOd || !dataDo) return setError('Podaj daty');
    if (dataOd > dataDo) return setError('Data od nie może być późniejsza niż do');

    setSaving(true);
    try {
      await onSave({
        osoba_nazwa,
        user_id: mode === 'user' ? parseInt(userId) : null,
        typ,
        data_od: dataOd,
        data_do: dataDo,
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.error || 'Błąd zapisu');
      setSaving(false);
    }
  };

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <span style={s.modalTitle}>Dodaj urlop</span>
          <button style={s.modalClose} onClick={onClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          <div style={s.field}>
            <label style={s.label}>Typ nieobecności</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button style={typ === 'URLOP' ? s.tabActive : s.tab} onClick={() => setTyp('URLOP')}>🌴 Urlop</button>
              <button style={typ === 'L4'    ? s.tabActive : s.tab} onClick={() => setTyp('L4')}>🤒 L4</button>
            </div>
          </div>
          <div style={s.field}>
            <label style={s.label}>Osoba</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <button
                style={mode === 'user' ? s.tabActive : s.tab}
                onClick={() => setMode('user')}
              >Użytkownik systemu</button>
              <button
                style={mode === 'other' ? s.tabActive : s.tab}
                onClick={() => setMode('other')}
              >Inna osoba</button>
            </div>
            {mode === 'user' ? (
              <select style={s.input} value={userId} onChange={e => setUserId(e.target.value)}>
                {users.map(u => <option key={u.id} value={u.id}>{u.imie}</option>)}
              </select>
            ) : (
              <input
                style={s.input}
                placeholder="Imię i nazwisko"
                value={customName}
                onChange={e => setCustomName(e.target.value)}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...s.field, flex: 1 }}>
              <label style={s.label}>Data od</label>
              <input style={s.input} type="date" value={dataOd} onChange={e => {
                setDataOd(e.target.value);
                if (!dataDo || dataDo < e.target.value) setDataDo(e.target.value);
              }} />
            </div>
            <div style={{ ...s.field, flex: 1 }}>
              <label style={s.label}>Data do</label>
              <input style={s.input} type="date" value={dataDo} onChange={e => setDataDo(e.target.value)} />
            </div>
          </div>
          {error && <div style={s.error}>{error}</div>}
        </div>
        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose}>Anuluj</button>
          <button style={s.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisuję...' : 'Dodaj'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Month table ───────────────────────────────────────────────────────────────
function MonthTable({ year, monthIdx, urlopyByDay, zamknieciaSet, holidays, isAdmin, onAddZamkniecie, onRemoveZamkniecie, onRemoveUrlop, zamknieciaMap, colorMap }) {
  const [hoverDay, setHoverDay]       = useState(null);
  const [editingDay, setEditingDay]   = useState(null);
  const [editingTyp, setEditingTyp]   = useState('ZAMKNIECIE');
  const [menuDay, setMenuDay]         = useState(null);
  const [opisInput, setOpisInput]     = useState('');
  const [saving, setSaving]           = useState(false);

  const days = daysInMonth(year, monthIdx);

  const handlePlusClick = (dayStr) => {
    setMenuDay(prev => prev === dayStr ? null : dayStr);
  };

  const startEditing = (dayStr, typ) => {
    setMenuDay(null);
    setEditingDay(dayStr);
    setEditingTyp(typ);
    setOpisInput('');
  };

  const handleSaveZamkniecie = async (dayStr) => {
    setSaving(true);
    try {
      await onAddZamkniecie(dayStr, opisInput, editingTyp);
      setEditingDay(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.monthCard}>
      <div style={s.monthTitle}>{MONTH_NAMES[monthIdx]} <span style={s.monthTitleEn}>/ {MONTH_NAMES_EN[monthIdx]}</span></div>
      <table style={s.monthTable}>
        <tbody>
          {Array.from({ length: days }, (_, i) => {
            const day  = i + 1;
            const date = new Date(year, monthIdx, day);
            const dow  = date.getDay(); // 0=Sun
            const iso  = toIso(date);
            const holidayName = holidays[iso] || null;
            const isHoliday  = !!holidayName;
            const isSunday   = dow === 0;
            const isSaturday = dow === 6;
            const zamkniecie = zamknieciaMap[iso];
            const isClosed   = !!zamkniecie && zamkniecie.typ !== 'NOTATKA';
            const hasNotatka = !!zamkniecie && zamkniecie.typ === 'NOTATKA';
            const persons    = urlopyByDay[iso] || [];

            let rowBg = 'transparent';
            if (isClosed)                rowBg = 'rgba(255,140,0,0.13)';
            else if (isHoliday || isSunday) rowBg = 'rgba(255,80,80,0.10)';
            else if (isSaturday)         rowBg = 'rgba(88,166,255,0.10)';

            let dayColor = '#c9d1d9';
            if (isClosed)                dayColor = '#ff8c00';
            else if (isHoliday || isSunday) dayColor = '#ff6b6b';
            else if (isSaturday)         dayColor = '#58a6ff';

            return (
              <tr
                key={day}
                style={{ background: rowBg, borderBottom: '1px solid #21262d' }}
                onMouseEnter={() => setHoverDay(iso)}
                onMouseLeave={() => { setHoverDay(null); setMenuDay(null); }}
              >
                <td style={{ ...s.tdDay, color: dayColor }}>
                  {String(day).padStart(2, '0')} <span style={s.dowLabel}>{DAY_NAMES[dow]}</span>
                </td>
                <td style={s.tdPersons}>
                  {(isHoliday || isSunday) && holidayName && (
                    <span style={s.holidayLabel}>{holidayName}</span>
                  )}
                  {isClosed && (
                    <span style={s.closedLabel}>
                      {zamkniecie?.opis || 'Zamknięte'}
                      {isAdmin && (
                        <button style={s.closedRemove} onClick={() => onRemoveZamkniecie(zamkniecie.id)} title="Usuń">×</button>
                      )}
                    </span>
                  )}
                  {hasNotatka && (
                    <span style={s.notatkaDayLabel}>
                      {zamkniecie.opis || 'Notatka'}
                      {isAdmin && (
                        <button style={s.closedRemove} onClick={() => onRemoveZamkniecie(zamkniecie.id)} title="Usuń">×</button>
                      )}
                    </span>
                  )}
                  {persons.map(p => {
                    const pc = getPersonColor(p.osoba_nazwa, p.user_id, colorMap);
                    return (
                      <span key={p.id} style={{ ...s.badge, background: pc.bg, color: pc.color, borderColor: pc.bg }}>
                        {p.osoba_nazwa}{p.typ === 'L4' ? ' L4' : ''}
                        {isAdmin && (
                          <button style={{ ...s.badgeRemove, color: pc.color }} onClick={() => onRemoveUrlop(p.id)} title="Usuń">×</button>
                        )}
                      </span>
                    );
                  })}
                  {isAdmin && !isClosed && !hasNotatka && editingDay !== iso && hoverDay === iso && menuDay !== iso && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <button style={s.plusBtn} onClick={() => handlePlusClick(iso)} title="Dodaj">+</button>
                    </div>
                  )}
                  {isAdmin && menuDay === iso && (
                    <div style={s.plusMenu}>
                      <button style={s.plusMenuItem} onClick={() => startEditing(iso, 'ZAMKNIECIE')}>🔒 Zamknięcie firmy</button>
                      <button style={s.plusMenuItem} onClick={() => startEditing(iso, 'NOTATKA')}>📝 Notatka</button>
                    </div>
                  )}
                  {isAdmin && editingDay === iso && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input
                        autoFocus
                        style={s.inlineInput}
                        placeholder={editingTyp === 'ZAMKNIECIE' ? 'Opis zamknięcia (opcj.)' : 'Treść notatki'}
                        value={opisInput}
                        onChange={e => setOpisInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveZamkniecie(iso);
                          if (e.key === 'Escape') setEditingDay(null);
                        }}
                      />
                      <button style={s.inlineSave} onClick={() => handleSaveZamkniecie(iso)} disabled={saving}>
                        {saving ? '…' : '✓'}
                      </button>
                      <button style={s.inlineCancel} onClick={() => setEditingDay(null)}>✕</button>
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Month grid (compact view) ─────────────────────────────────────────────────
const GRID_DOW = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'];

function MonthGrid({ year, monthIdx, urlopyByDay, holidays, isAdmin, onAddZamkniecie, onRemoveZamkniecie, onRemoveUrlop, zamknieciaMap, colorMap }) {
  const [hoverDay, setHoverDay]     = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [editingTyp, setEditingTyp] = useState('ZAMKNIECIE');
  const [menuDay, setMenuDay]       = useState(null);
  const [opisInput, setOpisInput]   = useState('');
  const [saving, setSaving]         = useState(false);

  const handlePlusClick = (iso) => setMenuDay(prev => prev === iso ? null : iso);
  const startEditing = (iso, typ) => { setMenuDay(null); setEditingDay(iso); setEditingTyp(typ); setOpisInput(''); };
  const handleSave = async (iso) => {
    setSaving(true);
    try { await onAddZamkniecie(iso, opisInput, editingTyp); setEditingDay(null); }
    finally { setSaving(false); }
  };

  const firstDow = (new Date(year, monthIdx, 1).getDay() + 6) % 7; // Mon=0
  const totalDays = daysInMonth(year, monthIdx);
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: totalDays }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7));

  return (
    <div style={sg.monthCard}>
      <div style={sg.monthTitle}>{MONTH_NAMES[monthIdx]} <span style={sg.monthTitleEn}>/ {MONTH_NAMES_EN[monthIdx]}</span></div>
      <table style={sg.table}>
        <thead>
          <tr>
            {GRID_DOW.map((d, i) => (
              <th key={d} style={{ ...sg.th, color: i === 5 ? '#58a6ff' : i === 6 ? '#ff6b6b' : '#8b949e' }}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => {
                if (!day) return <td key={di} style={sg.tdEmpty} />;
                const date = new Date(year, monthIdx, day);
                const dow  = date.getDay();
                const iso  = toIso(date);
                const holidayName = holidays[iso] || null;
                const isHoliday  = !!holidayName;
                const isSunday   = dow === 0;
                const isSaturday = dow === 6;
                const zamkniecie = zamknieciaMap[iso];
                const isClosed   = !!zamkniecie && zamkniecie.typ !== 'NOTATKA';
                const hasNotatka = !!zamkniecie && zamkniecie.typ === 'NOTATKA';
                const persons    = urlopyByDay[iso] || [];

                let cellBg = 'transparent';
                if (isClosed)                  cellBg = 'rgba(255,140,0,0.13)';
                else if (isHoliday || isSunday) cellBg = 'rgba(255,80,80,0.10)';
                else if (isSaturday)            cellBg = 'rgba(88,166,255,0.08)';

                let dayColor = '#c9d1d9';
                if (isClosed)                  dayColor = '#ff8c00';
                else if (isHoliday || isSunday) dayColor = '#ff6b6b';
                else if (isSaturday)            dayColor = '#58a6ff';

                return (
                  <td
                    key={di}
                    style={{ ...sg.td, background: cellBg }}
                    onMouseEnter={() => setHoverDay(iso)}
                    onMouseLeave={() => { setHoverDay(null); setMenuDay(null); }}
                  >
                    <div style={{ ...sg.dayNum, color: dayColor }}>{day}</div>
                    {(isHoliday || isClosed || hasNotatka) && (
                      <div style={sg.cellLabel}>
                        {isClosed ? (zamkniecie.opis || 'Zamknięte') : hasNotatka ? (zamkniecie.opis || 'Notatka') : holidayName}
                        {isAdmin && (isClosed || hasNotatka) && (
                          <button style={sg.cellRemoveBtn} onClick={() => onRemoveZamkniecie(zamkniecie.id)}>×</button>
                        )}
                      </div>
                    )}
                    <div style={sg.badgesWrap}>
                      {persons.map(p => {
                        const pc = getPersonColor(p.osoba_nazwa, p.user_id, colorMap);
                        return (
                          <span key={p.id} style={{ ...sg.badge, background: pc.bg, color: pc.color }}>
                            {p.osoba_nazwa.split(' ')[0]}{p.typ === 'L4' ? ' L4' : ''}
                            {isAdmin && <button style={{ ...sg.badgeRemove, color: pc.color }} onClick={() => onRemoveUrlop(p.id)}>×</button>}
                          </span>
                        );
                      })}
                    </div>
                    {isAdmin && !isClosed && !hasNotatka && editingDay !== iso && hoverDay === iso && menuDay !== iso && (
                      <button style={sg.plusBtn} onClick={() => handlePlusClick(iso)}>+</button>
                    )}
                    {isAdmin && menuDay === iso && (
                      <div style={sg.plusMenu}>
                        <button style={sg.plusMenuItem} onClick={() => startEditing(iso, 'ZAMKNIECIE')}>🔒 Zamknięcie</button>
                        <button style={sg.plusMenuItem} onClick={() => startEditing(iso, 'NOTATKA')}>📝 Notatka</button>
                      </div>
                    )}
                    {isAdmin && editingDay === iso && (
                      <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                        <input
                          autoFocus
                          style={sg.inlineInput}
                          placeholder={editingTyp === 'ZAMKNIECIE' ? 'Opis...' : 'Notatka...'}
                          value={opisInput}
                          onChange={e => setOpisInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleSave(iso); if (e.key === 'Escape') setEditingDay(null); }}
                        />
                        <button style={sg.inlineSave} onClick={() => handleSave(iso)} disabled={saving}>{saving ? '…' : '✓'}</button>
                        <button style={sg.inlineCancel} onClick={() => setEditingDay(null)}>✕</button>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AbsenceSummary({ year, urlopy, zamkniecia, holidays, colorMap }) {
  const people = new Map();

  urlopy.forEach(u => {
    const name = u.osoba_nazwa?.trim();
    if (!name) return;
    const key = getPersonKey(u.user_id, name);
    if (!people.has(key)) people.set(key, { userId: u.user_id, name, urlop: new Set(), l4: new Set() });
    const person = people.get(key);
    const target = u.typ === 'L4' ? person.l4 : person.urlop;
    getWorkDatesInYear(u.data_od, u.data_do, year, holidays).forEach(iso => target.add(iso));
  });

  const closedDays = zamkniecia
    .filter(z => z.typ !== 'NOTATKA')
    .map(z => toIso(new Date(z.data)))
    .filter(iso => Number(iso.slice(0, 4)) === year && isWorkdayIso(iso, holidays));

  people.forEach(person => {
    closedDays.forEach(iso => {
      if (!person.l4.has(iso)) person.urlop.add(iso);
    });
  });

  const rows = [...people.values()]
    .map(person => ({
      ...person,
      urlopDays: person.urlop.size,
      l4Days: person.l4.size,
    }))
    .filter(person => person.urlopDays > 0 || person.l4Days > 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'pl'));

  if (rows.length === 0) return null;

  const totalUrlop = rows.reduce((sum, row) => sum + row.urlopDays, 0);
  const totalL4 = rows.reduce((sum, row) => sum + row.l4Days, 0);

  return (
    <div style={s.summaryWrap}>
      <div style={s.summaryTitle}>Podsumowanie urlopów i L4 (zamknięcia firmy liczone są jako dzień urlopu)</div>
      <table style={s.summaryTable}>
        <thead>
          <tr>
            <th style={s.summaryTh}>Osoba</th>
            <th style={{ ...s.summaryTh, ...s.summaryNum }}>Dni urlopu</th>
            <th style={{ ...s.summaryTh, ...s.summaryNum }}>Dni L4</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const pc = getPersonColor(row.name, row.userId, colorMap);
            return (
              <tr key={getPersonKey(row.userId, row.name)}>
                <td style={s.summaryTd}>
                  <span style={{ ...s.summaryBadge, background: pc.bg, color: pc.color }}>{row.name}</span>
                </td>
                <td style={{ ...s.summaryTd, ...s.summaryNum }}>{row.urlopDays}</td>
                <td style={{ ...s.summaryTd, ...s.summaryNum }}>{row.l4Days}</td>
              </tr>
            );
          })}
          <tr>
            <td style={{ ...s.summaryTd, ...s.summaryTotal }}>Razem</td>
            <td style={{ ...s.summaryTd, ...s.summaryNum, ...s.summaryTotal }}>{totalUrlop}</td>
            <td style={{ ...s.summaryTd, ...s.summaryNum, ...s.summaryTotal }}>{totalL4}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function Urlopy({ user }) {
  const isAdmin = user.rola === 'ADMIN';
  const currentYear = new Date().getFullYear();

  const [year, setYear]             = useState(currentYear);
  const [viewMode, setViewMode]     = useState('list'); // 'list' | 'grid'
  const [urlopy, setUrlopy]         = useState([]);
  const [zamkniecia, setZamkniecia] = useState([]);
  const [users, setUsers]           = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [loading, setLoading]       = useState(true);

  const holidays = getPolishHolidays(year);

  // Buduj mapę kolorów: Asia=0, reszta kolejno wg users + unikalnych nazw z urlopy
  const personColorMap = {};
  let colorIdx = 1;
  const asiaUserId = users.find(u => u.imie?.trim().toLowerCase().startsWith('asia'))?.id;
  if (asiaUserId) personColorMap[`user:${asiaUserId}`] = 0;
  users.forEach(u => {
    if (u.id === asiaUserId) return;
    personColorMap[`user:${u.id}`] = colorIdx++;
  });
  // osoby bez konta — sekwencyjnie z urlopy
  urlopy.forEach(u => {
    if (u.user_id != null) return;
    const name = u.osoba_nazwa?.trim() || '';
    if (name.toLowerCase().startsWith('asia')) return;
    const key = `name:${name}`;
    if (personColorMap[key] === undefined) personColorMap[key] = colorIdx++;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, z, us] = await Promise.all([
        api.get('/urlopy'),
        api.get('/zamkniecia'),
        isAdmin ? api.get('/users') : Promise.resolve({ data: [] }),
      ]);
      setUrlopy(u.data);
      setZamkniecia(z.data);
      setUsers(us.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  // Build day → [urlop] map for current year
  const urlopyByDay = {};
  urlopy.forEach(u => {
    const od = new Date(u.data_od);
    const do_ = new Date(u.data_do);
    for (let d = new Date(od); d <= do_; d.setDate(d.getDate() + 1)) {
      if (d.getFullYear() !== year) continue;
      const iso = toIso(d);
      if (!urlopyByDay[iso]) urlopyByDay[iso] = [];
      urlopyByDay[iso].push(u);
    }
  });

  // Build closed days set/map
  const zamknieciaSet = new Set();
  const zamknieciaMap = {};
  zamkniecia.forEach(z => {
    const iso = toIso(new Date(z.data));
    zamknieciaSet.add(iso);
    zamknieciaMap[iso] = z;
  });

  const handleAddUrlop = async (data) => {
    await api.post('/urlopy', data);
    await load();
  };

  const handleRemoveUrlop = async (id) => {
    await api.delete(`/urlopy/${id}`);
    await load();
  };

  const handleAddZamkniecie = async (data, opis, typ) => {
    await api.post('/zamkniecia', { data, opis, typ });
    await load();
  };

  const handleRemoveZamkniecie = async (id) => {
    await api.delete(`/zamkniecia/${id}`);
    await load();
  };

  if (loading) return <div style={s.loading}>Ładowanie...</div>;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.pageTitle}>📅 Kalendarz</span>
          <div style={s.yearNav}>
            <button style={s.yearBtn} onClick={() => setYear(y => y - 1)}>‹</button>
            <span style={s.yearLabel}>{year}</span>
            <button style={s.yearBtn} onClick={() => setYear(y => y + 1)}>›</button>
          </div>
          <div style={s.viewToggle}>
            <button style={viewMode === 'list' ? s.viewBtnActive : s.viewBtn} onClick={() => setViewMode('list')} title="Widok listy">☰</button>
            <button style={viewMode === 'grid' ? s.viewBtnActive : s.viewBtn} onClick={() => setViewMode('grid')} title="Widok siatki">⊞</button>
          </div>
        </div>
        <div style={s.headerRight}>
          <div style={s.legend}>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: 'rgba(255,140,0,0.5)' }} />Zamknięte</span>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: 'rgba(255,80,80,0.4)' }} />Niedziela/Święto</span>
            <span style={s.legendItem}><span style={{ ...s.legendDot, background: 'rgba(88,166,255,0.4)' }} />Sobota</span>
          </div>
          {isAdmin && (
            <button style={s.btnPrimary} onClick={() => setShowModal(true)}>+ Dodaj urlop</button>
          )}
        </div>
      </div>

      {/* Grid 3×4 */}
      <div style={viewMode === 'grid' ? s.gridWide : s.grid}>
        {Array.from({ length: 12 }, (_, i) => (
          viewMode === 'grid' ? (
            <MonthGrid
              key={i}
              year={year}
              monthIdx={i}
              urlopyByDay={urlopyByDay}
              zamknieciaMap={zamknieciaMap}
              holidays={holidays}
              isAdmin={isAdmin}
              colorMap={personColorMap}
              onAddZamkniecie={handleAddZamkniecie}
              onRemoveZamkniecie={handleRemoveZamkniecie}
              onRemoveUrlop={handleRemoveUrlop}
            />
          ) : (
            <MonthTable
              key={i}
              year={year}
              monthIdx={i}
              urlopyByDay={urlopyByDay}
              zamknieciaSet={zamknieciaSet}
              zamknieciaMap={zamknieciaMap}
              holidays={holidays}
              isAdmin={isAdmin}
              colorMap={personColorMap}
              onAddZamkniecie={handleAddZamkniecie}
              onRemoveZamkniecie={handleRemoveZamkniecie}
              onRemoveUrlop={handleRemoveUrlop}
            />
          )
        ))}
      </div>

      <AbsenceSummary
        year={year}
        urlopy={urlopy}
        zamkniecia={zamkniecia}
        holidays={holidays}
        colorMap={personColorMap}
      />

      {/* Modal */}
      {showModal && (
        <AddUrlopModal
          users={users}
          onSave={handleAddUrlop}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page:    { padding: '0', color: '#c9d1d9', minHeight: '100%' },
  loading: { padding: 40, color: '#8b949e', textAlign: 'center' },

  header:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, position: 'sticky', top: 0, zIndex: 10, background: '#0d1117', padding: '14px 24px', borderBottom: '1px solid #30363d' },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 16 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  pageTitle:   { fontSize: 20, fontWeight: 700, color: '#e6edf3' },

  yearNav:   { display: 'flex', alignItems: 'center', gap: 6 },
  yearLabel: { fontSize: 16, fontWeight: 600, color: '#e6edf3', minWidth: 44, textAlign: 'center' },
  yearBtn:   { background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 16 },

  legend:     { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#8b949e' },
  legendDot:  { width: 12, height: 12, borderRadius: 2, display: 'inline-block' },

  grid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, padding: '20px 24px' },
  gridWide: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, padding: '20px 24px' },

  summaryWrap:  { margin: '0 24px 24px', background: '#161b22', border: '1px solid #30363d', borderRadius: 8, overflow: 'hidden' },
  summaryTitle: { padding: '10px 12px', fontWeight: 700, fontSize: 14, color: '#e6edf3', background: '#1c2128', borderBottom: '1px solid #30363d' },
  summaryTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  summaryTh:    { padding: '8px 12px', textAlign: 'left', color: '#8b949e', borderBottom: '1px solid #30363d', fontWeight: 600 },
  summaryTd:    { padding: '8px 12px', borderBottom: '1px solid #21262d', color: '#c9d1d9' },
  summaryNum:   { textAlign: 'right', width: 120 },
  summaryBadge: { display: 'inline-flex', borderRadius: 4, padding: '2px 7px', fontSize: 12, fontWeight: 600 },
  summaryTotal: { fontWeight: 700, color: '#e6edf3', background: '#1c2128' },

  viewToggle:    { display: 'flex', gap: 2 },
  viewBtn:       { background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 4, padding: '3px 9px', cursor: 'pointer', fontSize: 15 },
  viewBtnActive: { background: '#1c3a5e', border: '1px solid #1f4068', color: '#58a6ff', borderRadius: 4, padding: '3px 9px', cursor: 'pointer', fontSize: 15 },

  monthCard:  { background: '#161b22', border: '1px solid #30363d', borderRadius: 8, overflow: 'hidden' },
  monthTitle:   { padding: '8px 12px', fontWeight: 700, fontSize: 13, color: '#e6edf3', background: '#1c2128', borderBottom: '1px solid #30363d' },
  monthTitleEn: { fontWeight: 700, fontSize: 13, color: '#8b949e' },
  monthTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },

  tdDay:    { padding: '3px 8px', whiteSpace: 'nowrap', fontWeight: 600, width: 52, color: '#c9d1d9', userSelect: 'none' },
  dowLabel: { fontWeight: 400, color: '#8b949e', fontSize: 11 },
  tdPersons:{ padding: '3px 6px', display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minHeight: 26, position: 'relative' },
  holidayLabel: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', pointerEvents: 'none', whiteSpace: 'nowrap' },

  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    background: '#1c3a5e', color: '#58a6ff', border: '1px solid #1f4068',
    borderRadius: 4, padding: '1px 6px', fontSize: 11, fontWeight: 500,
  },
  closedLabel:      { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', pointerEvents: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 },
  closedRemove:     { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: '0 1px', lineHeight: 1, fontSize: 13, pointerEvents: 'all' },
  notatkaDayLabel:  { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', pointerEvents: 'none', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 },
  plusMenu:     { position: 'absolute', zIndex: 50, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column', minWidth: 160 },
  plusMenuItem: { background: 'none', border: 'none', color: '#c9d1d9', padding: '7px 12px', cursor: 'pointer', fontSize: 12, textAlign: 'left', whiteSpace: 'nowrap' },
  badgeRemove: {
    background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
    padding: '0 1px', lineHeight: 1, fontSize: 13, opacity: 0.7,
  },

  plusBtn: {
    background: 'none', border: '1px solid #30363d', color: '#8b949e',
    borderRadius: 3, cursor: 'pointer', padding: '0 5px', fontSize: 14, lineHeight: '18px',
  },
  inlineInput:  { background: '#0d1117', border: '1px solid #388bfd', borderRadius: 3, color: '#c9d1d9', padding: '1px 5px', fontSize: 11, width: 120, outline: 'none' },
  inlineSave:   { background: '#238636', border: 'none', color: 'white', borderRadius: 3, cursor: 'pointer', padding: '1px 6px', fontSize: 12 },
  inlineCancel: { background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 3, cursor: 'pointer', padding: '1px 5px', fontSize: 12 },

  // Modal
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:        { background: '#161b22', border: '1px solid #30363d', borderRadius: 8, width: 380, maxWidth: '95vw' },
  modalHeader:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #30363d' },
  modalTitle:   { fontWeight: 700, fontSize: 15, color: '#e6edf3' },
  modalClose:   { background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: 16, padding: 2 },
  modalBody:    { padding: '16px' },
  modalFooter:  { padding: '12px 16px', borderTop: '1px solid #30363d', display: 'flex', justifyContent: 'flex-end', gap: 8 },

  field:  { marginBottom: 12 },
  label:  { display: 'block', fontSize: 12, color: '#8b949e', marginBottom: 4 },
  input:  { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 4, color: '#c9d1d9', padding: '6px 8px', fontSize: 13, boxSizing: 'border-box', outline: 'none' },
  error:  { color: '#f85149', fontSize: 12, marginTop: 8 },

  tab:       { background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  tabActive: { background: '#1c3a5e', border: '1px solid #1f4068', color: '#58a6ff', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },

  btnPrimary:   { background: '#238636', border: 'none', color: 'white', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  btnSecondary: { background: '#21262d', border: '1px solid #30363d', color: '#c9d1d9', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 13 },
};

// ── Styles: compact grid view ─────────────────────────────────────────────────
const sg = {
  monthCard:  { background: '#161b22', border: '1px solid #30363d', borderRadius: 8, overflow: 'hidden' },
  monthTitle:   { padding: '6px 8px', fontWeight: 700, fontSize: 12, color: '#e6edf3', background: '#1c2128', borderBottom: '1px solid #30363d', textAlign: 'center' },
  monthTitleEn: { fontWeight: 700, fontSize: 12, color: '#8b949e' },
  table:      { width: '100%', borderCollapse: 'collapse', fontSize: 10, tableLayout: 'fixed' },
  th:         { padding: '3px 2px', textAlign: 'center', fontWeight: 600, fontSize: 10, borderBottom: '1px solid #30363d', background: '#1c2128' },
  td:         { padding: '3px 4px', verticalAlign: 'top', border: '1px solid #21262d', position: 'relative', height: 64 },
  tdEmpty:    { border: '1px solid #21262d', background: '#0d1117' },
  dayNum:     { fontWeight: 700, fontSize: 11, marginBottom: 1 },
  cellLabel:  { fontSize: 9, color: 'rgba(255,255,255,0.42)', fontStyle: 'italic', lineHeight: 1.2, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 2, wordBreak: 'break-word' },
  cellRemoveBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', padding: '0 1px', lineHeight: 1, fontSize: 10, pointerEvents: 'all', flexShrink: 0 },
  badgesWrap: { display: 'flex', flexWrap: 'wrap', gap: 2 },
  badge:      { display: 'inline-flex', alignItems: 'center', gap: 1, background: '#1c3a5e', color: '#58a6ff', borderRadius: 3, padding: '0 3px', fontSize: 9, fontWeight: 500 },
  badgeRemove:{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: '0 1px', lineHeight: 1, fontSize: 9, opacity: 0.7 },
  plusBtn:    { position: 'absolute', bottom: 2, right: 2, background: 'none', border: '1px solid #30363d', color: '#8b949e', borderRadius: 3, cursor: 'pointer', padding: '0 4px', fontSize: 11, lineHeight: '15px' },
  plusMenu:   { position: 'absolute', zIndex: 50, background: '#161b22', border: '1px solid #30363d', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column', minWidth: 130, right: 0, bottom: 20 },
  plusMenuItem:{ background: 'none', border: 'none', color: '#c9d1d9', padding: '6px 10px', cursor: 'pointer', fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap' },
  inlineInput: { background: '#0d1117', border: '1px solid #388bfd', borderRadius: 3, color: '#c9d1d9', padding: '1px 4px', fontSize: 9, width: '100%', outline: 'none' },
  inlineSave:  { background: '#238636', border: 'none', color: 'white', borderRadius: 3, cursor: 'pointer', padding: '1px 4px', fontSize: 9 },
  inlineCancel:{ background: '#21262d', border: '1px solid #30363d', color: '#8b949e', borderRadius: 3, cursor: 'pointer', padding: '1px 4px', fontSize: 9 },
};
